import { Link } from "react-router-dom";
import { 
  Upload, 
  ClipboardCheck, 
  FileCheck, 
  Send,
  FileText,
  CreditCard,
  Home,
  GraduationCap,
  Briefcase,
  Heart,
  Check,
  ExternalLink,
  Printer
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const PORTAL_URL = process.env.REACT_APP_PORTAL_URL;

const steps = [
  { 
    icon: Upload, 
    number: "01", 
    title: "Create Your Portal Account", 
    description: "Sign up for secure access. We'll send you an email to get started with your personalized portal." 
  },
  { 
    icon: ClipboardCheck, 
    number: "02", 
    title: "Upload Your Documents", 
    description: "Securely upload your W-2s, 1099s, and other tax documents. Our checklist guides you through what's needed." 
  },
  { 
    icon: FileCheck, 
    number: "03", 
    title: "We Prepare & Review", 
    description: "Our team prepares your return, maximizes deductions, and reviews for accuracy." 
  },
  { 
    icon: Send, 
    number: "04", 
    title: "E-File & Year-Round Support", 
    description: "We e-file your return, provide copies, and offer support throughout the year." 
  },
];

const documentCategories = [
  {
    title: "Income Documents",
    icon: FileText,
    items: [
      "W-2 forms (all employers)",
      "1099-NEC / 1099-MISC (freelance/contractor income)",
      "1099-INT / 1099-DIV (interest/dividends)",
      "1099-B (investment sales)",
      "1099-R (retirement distributions)",
      "1099-G (unemployment, state refund)",
      "K-1 forms (partnerships, S-Corps, trusts)"
    ]
  },
  {
    title: "Deduction Documents",
    icon: CreditCard,
    items: [
      "Mortgage interest (Form 1098)",
      "Property tax statements",
      "Charitable donation receipts",
      "Medical expense records",
      "Student loan interest (Form 1098-E)",
      "State/local tax payments"
    ]
  },
  {
    title: "Business/Self-Employed",
    icon: Briefcase,
    items: [
      "Business income and expenses summary",
      "Vehicle mileage log",
      "Home office measurements",
      "Equipment purchase receipts",
      "Professional service fees",
      "Business insurance payments"
    ]
  },
  {
    title: "Other Important Items",
    icon: Home,
    items: [
      "Prior year tax return",
      "Valid photo ID (driver's license)",
      "Social Security cards (all dependents)",
      "Bank account info for direct deposit",
      "Health insurance forms (1095-A/B/C)",
      "Childcare provider info (name, address, EIN)"
    ]
  }
];

const faqs = [
  {
    question: "What do I need to get started?",
    answer: "To get started, create a portal account and upload your income documents (W-2s, 1099s), photo ID, Social Security information, and your prior year tax return if available. Our document checklist in the portal will guide you through everything needed for your specific situation."
  },
  {
    question: "Can I file virtually? Do you meet in person?",
    answer: "Yes! We specialize in virtual tax preparation. Our secure portal allows you to upload documents, communicate with our team, and e-sign your return from anywhere. We serve clients nationwide. In-person meetings can be arranged for local NYC clients when necessary."
  },
  {
    question: "Do you help with prior-year returns?",
    answer: "Absolutely. We prepare prior-year returns for clients who need to catch up on unfiled taxes. This includes amended returns if you need to correct previously filed returns. Contact us to discuss your specific situation."
  },
  {
    question: "What if I received a notice from the IRS or state?",
    answer: "Don't panic! Tax notices are common and often routine. Upload the notice to your portal and our team will review it, explain what it means, and help you respond appropriately. We handle notices as part of our comprehensive service."
  },
  {
    question: "How do I access the client portal?",
    answer: "Visit our Client Portal page or click the 'Client Portal Login' button anywhere on our site. New clients can request an account, and existing clients can log in directly. Never email sensitive documents—always use the secure portal."
  },
  {
    question: "What are your payment options?",
    answer: "We offer flexible payment options including credit/debit cards and bank transfers. Payment details will be provided once your return is prepared. We focus on providing value and transparent pricing—no hidden fees or surprises."
  }
];

export default function TaxPrep() {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div data-testid="tax-prep-page">
      {/* Hero */}
      <section className="bg-navy text-white noise-overlay py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">
              Tax Preparation
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Simple, Secure Tax Filing
            </h1>
            <p className="font-body text-lg md:text-xl text-gray-300 mb-8">
              Our streamlined process makes tax preparation easy. Upload your documents, and we handle the rest.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-medium"
                data-testid="taxprep-portal-btn"
              >
                <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer">
                  Start Now
                  <ExternalLink className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-navy rounded-full px-8 py-6 text-lg font-medium"
                data-testid="taxprep-book-btn"
              >
                <a href="#book" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-booking")); }}>
                  Book Consultation
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">Our Process</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-4">
              How We Prepare Your Taxes
            </h2>
            <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto">
              A straightforward, stress-free process from start to finish.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div 
                key={step.number} 
                className="relative"
                data-testid={`process-step-${index + 1}`}
              >
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-gray-300 -translate-x-1/2"></div>
                )}
                <Card className="bg-white border-0 shadow-md h-full">
                  <CardContent className="p-6 text-center">
                    <div className="relative inline-block mb-6">
                      <div className="w-20 h-20 rounded-full bg-bone flex items-center justify-center mx-auto">
                        <step.icon className="w-8 h-8 text-bronze" />
                      </div>
                      <span className="absolute -top-2 -right-2 font-mono text-3xl font-bold text-bronze/30">
                        {step.number}
                      </span>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-navy mb-3">
                      {step.title}
                    </h3>
                    <p className="font-body text-gray-600 text-sm">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Document Checklist */}
      <section className="bg-white py-20 md:py-28 print:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-4">
            <div>
              <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">Document Checklist</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">
                What to Upload
              </h2>
            </div>
            <Button
              onClick={handlePrint}
              variant="outline"
              className="rounded-full font-medium print:hidden"
              data-testid="print-checklist-btn"
            >
              <Printer className="mr-2 w-4 h-4" />
              Print Checklist
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {documentCategories.map((category, index) => (
              <Card 
                key={category.title} 
                className="bg-bone border-0"
                data-testid={`document-category-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
                      <category.icon className="w-5 h-5 text-bronze" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-navy">
                      {category.title}
                    </h3>
                  </div>
                  <ul className="space-y-2">
                    {category.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 font-body text-gray-700">
                        <div className="w-5 h-5 rounded border border-gray-300 flex-shrink-0 mt-0.5 print:border-gray-400"></div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 p-6 bg-navy/5 rounded-2xl border border-navy/10">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-bronze/20 flex items-center justify-center flex-shrink-0">
                <Heart className="w-6 h-6 text-bronze" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-navy mb-2">
                  Security First
                </h3>
                <p className="font-body text-gray-600">
                  <strong>Never email sensitive documents like Social Security numbers or full tax returns.</strong> Always use our secure client portal to upload documents. Your information is encrypted and protected.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-bone py-20 md:py-28 print:hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">FAQ</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white rounded-xl border-0 shadow-sm px-6"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="font-body font-semibold text-navy hover:text-bronze py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="font-body text-gray-600 pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Band */}
      <section className="bg-navy text-white noise-overlay py-16 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
            Ready to File Your Taxes?
          </h2>
          <p className="font-body text-gray-300 mb-8 max-w-2xl mx-auto">
            Create your portal account today and experience hassle-free tax preparation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-medium"
              data-testid="taxprep-cta-portal"
            >
              <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer">
                Client Portal Login
                <ExternalLink className="ml-2 w-5 h-5" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-navy rounded-full px-8 py-6 text-lg font-medium"
              data-testid="taxprep-cta-book"
            >
              <a href="#book" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-booking")); }}>
                Book Free Consultation
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
